import { Component, computed, linkedSignal, resource, signal, viewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { NgSelectComponent, NgSelectModule } from "@ng-select/ng-select";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";

export interface StatusOption {
  id?: number;
  name: string;
  color?: string | null;
}

export interface AgGridStatusEditorOptions<TData> {
  loadOptions: (data: TData) => Promise<StatusOption[]>;
  readValue: (data: TData) => number;
  writeValue: (data: TData, value: number) => Promise<number>;
}

export type AgGridStatusEditorParams<TData> = ICellEditorParams<TData> & AgGridStatusEditorOptions<TData>;

@Component({
  selector: "ag-grid-status-editor",
  template: `
    <ng-select
      class="bm-join-item w-[300px]"
      [items]="options()"
      bindLabel="name"
      bindValue="id"
      [ngModel]="outputValue()"
      (ngModelChange)="outputValue.set($event)"
      [loading]="isLoading()"
      [required]="true"
    >
      <ng-template ng-label-tmp let-item="item">
        <div class="d-flex align-items-center gap-1">
          @if (item.closed) {
            <span class="fa fa-check-circle text-success"></span>
          }
          @if (item.canceled) {
            <span class="fa fa-ban text-error"></span>
          }
          @if (item.report_closed) {
            <span class="fa fa-hand-paper-o text-muted"></span>
          }
          <span>{{ item.name }}</span>
        </div>
      </ng-template>
    </ng-select>
  `,
  imports: [FormsModule, NgSelectModule, FontAwesomeModule],
  host: {
    class: "bm-join",
  },
})
export class AgGridStatusEditorComponent<TData> implements ICellEditorAngularComp {
  public static options<TData>(options: AgGridStatusEditorOptions<TData>) {
    return options;
  }

  protected params = signal<AgGridStatusEditorParams<TData>>(null);
  protected inputValue = computed(() => {
    const params = this.params();
    return params.readValue(params.data);
  });
  protected outputValue = linkedSignal<number>(() => this.inputValue());

  protected optionsResource = resource({
    request: this.params,
    loader: async ({ request }) => {
      return request.loadOptions(request.data);
    },
  });
  protected options = computed(() => (this.optionsResource.hasValue() ? this.optionsResource.value() : []));
  protected isSaving = signal(false);
  protected isLoading = this.optionsResource.isLoading;

  protected picker = viewChild(NgSelectComponent);

  public agInit(params: AgGridStatusEditorParams<TData>) {
    this.params.set(params);
  }

  public refresh(params: AgGridStatusEditorParams<TData>) {
    this.params.set(params);
    return true;
  }

  public getValue() {
    const value = this.outputValue();
    if (this.inputValue() !== value && this.params().writeValue) {
      this.params().writeValue(this.params().data, value);
    }
    return value;
  }

  public afterGuiAttached(): void {
    this.picker().open();
  }

  public isPopup(): boolean {
    return true; // editor doesn't fit into a cell, so it should be a popup
  }

  public cancel() {
    this.params().stopEditing();
  }

  private stopEditing() {
    this.params().stopEditing();
  }
}
